import { ChangeEvent, Dispatch, useEffect, useMemo, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';
import { useDispatch } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomCheckBox, CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { errorNormalizer, validateName } from '../../../shared/Helpers/functions';
import { DSU_LOCATION_MISMATCH_ERROR, DSU_TIME_MISMATCH_WARNING } from '../../../shared/Helpers/constants';
import { useSignatureDate, useSoapNoteFieldDisabled } from '../helpers';
import { useDirectSupervisionOptions, useIsTelehealth } from '../hook';
import { Behaviors, SignatureInput, Targets, TelehealthLocations, TimeInfo } from '../components';

import {
	ACTIONS,
	ActionProperties,
	ERRORS,
	directSuperVisionErrorReducer,
	initialDirectSupervisionErrorState,
} from './directSupervisionReducer';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	createSupervisedBehaviorTreatmentSession,
	getSupervisedSessionsInfo,
} from '../../../redux/State/clientSlice/sessionSlice';
import { GetSessionProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import {
	LocationTypes,
	UpdateDirectSupervisionProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { getClientBTs } from '../../../redux/State/clientSlice/userClientSlice';
import { SoapNotesStatuses } from '../../../redux/API/ClientAPIHelpers/dataCollectionProperties';

const DirectSupervisionData = ({
	data,
	setData,
	save,
}: {
	data: UpdateDirectSupervisionProperties;
	save: () => void;
	setData: Dispatch<ActionProperties>;
}) => {
	const signatureDate = useSignatureDate();
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();

	const [locations, setLocations] = useState<LocationTypes[]>([]);
	const [errors, setErrors] = useReducer(directSuperVisionErrorReducer, initialDirectSupervisionErrorState);

	const errorList = useSelector((state) => state.soapNote.error?.errors);
	const supervisedSessions = useSelector((state) => state.session.supervisedSessionInfo);
	const locationList = useSelector((state) => state.soapNote.locations);
	const { startTime, endTime } = useSelector((state) => state.session.sessionInfo);
	const clientBTs = useSelector((state) => state.userClient.clientBTs);

	const { feedbackList, participantsList } = useDirectSupervisionOptions();
	const { getFieldDisabled } = useSoapNoteFieldDisabled();
	const isTelehealth = useIsTelehealth(data.locationId);

	useEffect(() => {
		const clientId = params.get('clientId');
		if (!clientId) return;
		dispatch(getClientBTs(clientId));
	}, [dispatch, params]);

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [participantsList, setData, data]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	useEffect(() => {
		const sessionId = params.get('sessionId');
		if (!sessionId) return;
		dispatch(getSupervisedSessionsInfo(sessionId));
	}, [params, dispatch]);

	useEffect(() => {
		if (!locationList) return;

		const id = data.supervisedSessionId;

		if (!id) {
			setLocations(locationList);

			return;
		}

		const selectedSession = supervisedSessions.find((x) => x.id === id);

		if (!selectedSession) {
			setLocations(locationList);

			return;
		}

		// Only filter locations if BT note is submitted
		if (selectedSession.reportStatus?.status !== SoapNotesStatuses.submitted) {
			setLocations(locationList);

			return;
		}

		// Filter by location type if available
		const locationType = selectedSession.location?.locationType;

		if (!locationType) {
			setLocations(locationList);

			return;
		}

		const filteredLocations = locationList.filter((x) => x.locationType === locationType);
		setLocations(filteredLocations);
	}, [data, locationList, supervisedSessions]);

	const chooseSupervisedSessionHandler = (event: SelectChangeEvent<string>) => {
		const sessionId = params.get('sessionId');
		const { value } = event.target;

		if (!supervisedSessions?.length) {
			if (!sessionId) return;
			dispatch(createSupervisedBehaviorTreatmentSession({ sessionId, userId: value }))
				.then(unwrapResult)
				.then((response) => {
					dispatch(getSupervisedSessionsInfo(sessionId))
						.then(unwrapResult)
						.then(() => {
							const payload = response.id;
							setData({ payload, type: ACTIONS.setSupervisedSessionId });
						});
				})
				.catch(errorNormalizer);

			return;
		}

		setData({ payload: value, type: ACTIONS.setSupervisedSessionId });
	};

	const selectFeedbackHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setFeedbackResponses });
	};

	const promtedEffectivelyHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setData({
			type: ACTIONS.setPromtedEffectively,
			payload: checked,
		});
		if (!errors.PromtedEffectively) return;
		setErrors({ payload: false, type: ERRORS.PromtedEffectively });
	};

	const reinforcerImplementationHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setData({
			type: ACTIONS.setReinforcerImplementation,
			payload: checked,
		});
		if (!errors.ReinforcerImplementation) return;
		setErrors({ payload: false, type: ERRORS.ReinforcerImplementation });
	};

	const dataEnteredCorrectlyHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setData({
			type: ACTIONS.setDataEnteredCorrectly,
			payload: checked,
		});
		if (!errors.DataEnteredCorrectly) return;
		setErrors({ payload: false, type: ERRORS.DataEnteredCorrectly });
	};

	const implementationOfGoalHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setImplementationOfGoals });
		if (!errors.ImplementationOfGoal) return;
		setErrors({ payload: false, type: ERRORS.ImplementationOfGoal });
	};

	const responseToBehavioralFeedbackHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setResponseToBehavioralFeedback });
		if (!errors.ResponseToBehavioralFeedback) return;
		setErrors({ payload: false, type: ERRORS.ResponseToBehavioralFeedback });
	};

	const clientPerformanceHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setClientPerformance });
		if (!errors.ClientPerformance) return;
		setErrors({ payload: false, type: ERRORS.ClientPerformance });
	};

	const changesAndMeasurementsObservedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({
			payload: value,
			type: ACTIONS.setChangesAndMeasurementsObserved,
		});
		if (!errors.ChangesAndMeasurementsObserved) return;
		setErrors({ payload: false, type: ERRORS.ChangesAndMeasurementsObserved });
	};

	const descriptionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setDescriptionOfSession });
		if (!errors.DescriptionOfSession) return;
		setErrors({ payload: false, type: ERRORS.DescriptionOfSession });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const supervisedSessionNameNormilizer = (info: GetSessionProperties) => ({
		id: info.id,
		name: `${info.client.fullName} - ${parseWallClockTime(info.startTime).format(
			'MM/DD/YYYY hh:mm A',
		)} - ${parseWallClockTime(info.endTime).format('hh:mm A')} - ${
			info.isCompleted ? 'Session completed' : 'Session not completed'
		}`,
	});

	const supervisedBTSessionNormilizer = (id: string, fullName: string) => ({
		id,
		name: `${fullName} - ${parseWallClockTime(startTime).format('MM/DD/YYYY hh:mm A')} - ${parseWallClockTime(endTime).format(
			'hh:mm A',
		)} - Session not created`,
	});

	const selectLocationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const participantsHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setParticipants, payload: value });
		if (!errors.ParticipantResponses) return;
		setErrors({ payload: false, type: ERRORS.ParticipantResponses });
	};

	const feedbackOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setFeedbackOther });
		if (!errors.FeedbackOther) return;
		setErrors({ payload: false, type: ERRORS.FeedbackOther });
	};

	const participantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setDirectSupervisionParticipantOther });
		if (!errors.DirectSupervisionParticipantOther) return;
		setErrors({ payload: false, type: ERRORS.DirectSupervisionParticipantOther });
	};

	const isOtherSelectedForFeedback = data.feedbackResponses.find(
		(x) => feedbackList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForParticipants = data.participantResponses.find(
		(x) => participantsList.find((y) => y.id === x)?.text === 'Other',
	);

	const supervisedSessionData = supervisedSessions?.length
		? supervisedSessions.map((x) => supervisedSessionNameNormilizer(x))
		: clientBTs.map(({ id, fullName }) => supervisedBTSessionNormilizer(id, fullName));

	const isTimeMatching = useMemo(() => {
		const supervisedSessionInfo = supervisedSessions?.length
			? supervisedSessions.find((x) => x.id === data.supervisedSessionId)
			: null;

		if (!supervisedSessionInfo || !startTime || !endTime) return true;

		const dsuStart = parseWallClockTime(startTime);
		const dsuEnd = parseWallClockTime(endTime);
		const btStart = parseWallClockTime(supervisedSessionInfo.startTime);
		const btEnd = parseWallClockTime(supervisedSessionInfo.endTime);

		// DSU session must be within BT session boundaries
		const startIsValid = dsuStart.isAfter(btStart) || dsuStart.isSame(btStart, 'minute');
		const endIsValid = dsuEnd.isBefore(btEnd) || dsuEnd.isSame(btEnd, 'minute');

		return startIsValid && endIsValid;
	}, [supervisedSessions, startTime, endTime, data.supervisedSessionId]);

	return (
		<div>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			<CustomSelect
				label="Choose supervised session:"
				data={supervisedSessionData}
				value={data.supervisedSessionId as string}
				setValue={chooseSupervisedSessionHandler}
				className={isTimeMatching ? 'marginBottom16' : 'marginBottom32'}
				disabled={getFieldDisabled('supervisedSessionId')}
				error={errors.SupervisedSessionId || !isTimeMatching}
				errorMessage={isTimeMatching ? '' : DSU_TIME_MISMATCH_WARNING}
				description={supervisedSessions?.length ? '' : 'Selected session will be created automatically'}
				onBlur={save}
			/>
			<CustomSelect
				label="Location:"
				data={locations}
				value={data.locationId ? data.locationId : ''}
				error={errors.LocationId}
				errorMessage={errors.LocationId ? DSU_LOCATION_MISMATCH_ERROR : ''}
				setValue={selectLocationHandler}
				disabled={getFieldDisabled('locationId')}
				className="marginBottom16"
				onBlur={save}
			/>
			{isTelehealth && (
				<TelehealthLocations
					setData={setData}
					data={data}
					disabled={getFieldDisabled('clientTelehealthLocationId')}
					save={save}
				/>
			)}
			<MultipleSelect
				label="Session Participants:"
				data={participantsList}
				initialValue={data.participantResponses}
				setValue={participantsHandler}
				error={errors.ParticipantResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('participantResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.directSupervisionParticipantOther}
					onChange={participantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('participantOther')}
					onBlur={save}
					error={errors.DirectSupervisionParticipantOther}
				/>
			)}
			<CustomCheckBox
				item={{
					label: 'Prompted effectively?',
					checked: data.promptedEffectively,
					id: '0',
					disabled: getFieldDisabled('promptedEffectively'),
				}}
				onChange={promtedEffectivelyHandler}
				onBlur={save}
			/>
			<CustomCheckBox
				item={{
					label: `BT implemented the use of reinforcers properly according to the reinforcer rank order?`,
					checked: data.isReinforcerImplementationRight,
					id: '0',
					disabled: getFieldDisabled('isReinforcerImplementationRight'),
				}}
				onChange={reinforcerImplementationHandler}
				onBlur={save}
			/>
			<CustomCheckBox
				item={{
					label: 'Data entered correctly?',
					checked: data.dataEnteredCorrectly,
					id: '0',
					disabled: getFieldDisabled('dataEnteredCorrectly'),
				}}
				onChange={dataEnteredCorrectlyHandler}
				onBlur={save}
			/>
			<MultipleSelect
				label="Feedback provided to BT:"
				data={feedbackList}
				className="marginBottom16"
				setValue={selectFeedbackHandler}
				initialValue={data.feedbackResponses}
				error={errors.FeedbackResponses}
				disabled={getFieldDisabled('feedbackResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForFeedback && (
				<CustomInput
					label="If other, please describe: "
					value={data.feedbackOther}
					onChange={feedbackOtherHandler}
					className="marginBottom16"
					disabled={getFieldDisabled('feedbackOther')}
					onBlur={save}
					maxLength={40}
					error={errors.FeedbackOther}
				/>
			)}
			<CustomInput
				label="BT implementation of a goal from treatment plan:"
				value={data.implementationOfGoal}
				onChange={implementationOfGoalHandler}
				multiline
				error={errors.ImplementationOfGoal}
				className="marginBottom16"
				disabled={getFieldDisabled('implementationOfGoal')}
				onBlur={save}
			/>
			<CustomInput
				label="BT response to behavioral feedback:"
				value={data.responseToBehavioralFeedback}
				onChange={responseToBehavioralFeedbackHandler}
				multiline
				className="marginBottom16"
				error={errors.ResponseToBehavioralFeedback}
				disabled={getFieldDisabled('responseToBehavioralFeedback')}
				onBlur={save}
			/>
			<CustomInput
				label="Client performance on two (2) goals:"
				value={data.clientPerformance}
				onChange={clientPerformanceHandler}
				multiline
				className="marginBottom16"
				error={errors.ClientPerformance}
				disabled={getFieldDisabled('clientPerformance')}
				onBlur={save}
			/>
			<CustomInput
				label="Maladaptive behaviors, changes and measurements observed:"
				value={data.changesAndMeasurementsObserved}
				onChange={changesAndMeasurementsObservedHandler}
				multiline
				className="marginBottom16"
				error={errors.ChangesAndMeasurementsObserved}
				disabled={getFieldDisabled('changesAndMeasurementsObserved')}
			/>
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<CustomInput
				label="Description of session: "
				value={data.descriptionOfSession}
				onChange={descriptionHandler}
				error={errors.DescriptionOfSession}
				className="marginBottom8"
				multiline
				disabled={getFieldDisabled('descriptionOfSession')}
				onBlur={save}
			/>
			<SignatureInput
				value={data.signature}
				setValue={signatureHandler}
				error={errors.Signature}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date/time of signature:" type="psm" fontWeight="semiBold" />
				<Text title={signatureDate} type="psm" />
			</div>
		</div>
	);
};

export default DirectSupervisionData;
